/**
 * Battle Reporter
 * Collects and analyzes data from AI vs AI battles
 */

import { GameState } from "../../types";
import { AIPlayerType } from "../Core/IAIPlayer";
import { BattleMove, BattleState } from "../Core/AIOrchestrator";
import {
  BattleReport,
  GameAnalysis,
  GameSummary,
  PlayerSummary,
  MoveAnalysis,
  MoveQuality,
} from "./types";

export class BattleReporter {
  private battles: BattleReport[] = [];
  private currentBattle: BattleReport | null = null;

  /**
   * Start a new battle report
   */
  startBattle(lightPlayer: AIPlayerType, darkPlayer: AIPlayerType): void {
    this.currentBattle = {
      battleId: `battle_${Date.now()}`,
      timestamp: Date.now(),
      lightPlayer,
      darkPlayer,
      gamesPlayed: 0,
      lightWins: 0,
      darkWins: 0,
      draws: 0,
      games: [],
      overallStats: {
        light: this.createEmptyPlayerSummary(lightPlayer),
        dark: this.createEmptyPlayerSummary(darkPlayer),
      },
      headToHeadComparison: {
        winRate: { light: 0, dark: 0 },
        averageGameLength: 0,
        averageAccuracy: { light: 0, dark: 0 },
        blunderComparison: { light: 0, dark: 0 },
      },
    };

  }

  /**
   * Record a completed game
   */
  recordGame(battleState: BattleState): void {
    if (!this.currentBattle) return;

    const gameState = battleState.currentGameState;
    const winner =
      gameState.lightWon === true
        ? "light"
        : gameState.lightWon === false
        ? "dark"
        : "draw";

    // Create game analysis
    const gameAnalysis: GameAnalysis = {
      gameId: `game_${Date.now()}`,
      lightPlayer: battleState.lightPlayer,
      darkPlayer: battleState.darkPlayer,
      winner,
      totalMoves: battleState.moves.length,
      lightMoves: this.analyzeMoves(battleState.moves, "light"),
      darkMoves: this.analyzeMoves(battleState.moves, "dark"),
      summary: this.createGameSummary(battleState),
    };

    // Update battle statistics
    this.currentBattle.games.push(gameAnalysis);
    this.currentBattle.gamesPlayed++;

    if (winner === "light") this.currentBattle.lightWins++;
    else if (winner === "dark") this.currentBattle.darkWins++;
    else this.currentBattle.draws++;

    // Update overall stats
    this.updateOverallStats();

  }

  /**
   * Finish current battle and save report
   */
  finishBattle(): BattleReport | null {
    if (!this.currentBattle) return null;

    // Calculate head-to-head comparison
    this.calculateHeadToHead();

    // Save battle
    this.battles.push(this.currentBattle);


    const battle = this.currentBattle;
    this.currentBattle = null;

    return battle;
  }

  /**
   * Get current battle report
   */
  getCurrentBattle(): BattleReport | null {
    return this.currentBattle;
  }

  /**
   * Get all battle reports
   */
  getAllBattles(): BattleReport[] {
    return this.battles;
  }

  /**
   * Export battle report as JSON
   */
  exportBattleReport(battle: BattleReport): string {
    return JSON.stringify(battle, null, 2);
  }

  /**
   * Generate text summary of battle
   */
  generateSummary(battle: BattleReport): string {
    const lines: string[] = [];

    lines.push(`Battle Report: ${battle.lightPlayer} vs ${battle.darkPlayer}`);
    lines.push(`Games Played: ${battle.gamesPlayed}`);
    lines.push(`\nResults:`);
    lines.push(`  ${battle.lightPlayer}: ${battle.lightWins} wins`);
    lines.push(`  ${battle.darkPlayer}: ${battle.darkWins} wins`);
    lines.push(`  Draws: ${battle.draws}`);

    lines.push(`\nWin Rates:`);
    lines.push(
      `  ${battle.lightPlayer}: ${(battle.headToHeadComparison.winRate.light * 100).toFixed(
        1
      )}%`
    );
    lines.push(
      `  ${battle.darkPlayer}: ${(battle.headToHeadComparison.winRate.dark * 100).toFixed(
        1
      )}%`
    );

    lines.push(`\nAverage Game Length: ${battle.headToHeadComparison.averageGameLength} moves`);

    lines.push(`\nAccuracy:`);
    lines.push(
      `  ${battle.lightPlayer}: ${(
        battle.headToHeadComparison.averageAccuracy.light * 100
      ).toFixed(1)}%`
    );
    lines.push(
      `  ${battle.darkPlayer}: ${(
        battle.headToHeadComparison.averageAccuracy.dark * 100
      ).toFixed(1)}%`
    );

    lines.push(`\nBlunders:`);
    lines.push(
      `  ${battle.lightPlayer}: ${battle.headToHeadComparison.blunderComparison.light}`
    );
    lines.push(
      `  ${battle.darkPlayer}: ${battle.headToHeadComparison.blunderComparison.dark}`
    );

    return lines.join("\n");
  }

  /**
   * Analyze moves for a player
   */
  private analyzeMoves(moves: BattleMove[], player: "light" | "dark"): MoveAnalysis[] {
    return moves
      .filter((m) => m.player === player)
      .map((m) => this.analyzeMove(m));
  }

  /**
   * Analyze a single move (simplified - full analysis would use DeepAnalyzer)
   */
  private analyzeMove(move: BattleMove): MoveAnalysis {
    // Simplified analysis based on confidence and evaluation
    let quality: MoveQuality;

    if (move.confidence > 0.9) {
      quality = MoveQuality.BRILLIANT;
    } else if (move.confidence > 0.75) {
      quality = MoveQuality.GOOD;
    } else if (move.confidence > 0.5) {
      quality = MoveQuality.REGULAR;
    } else if (move.confidence > 0.3) {
      quality = MoveQuality.BAD;
    } else {
      quality = MoveQuality.BLUNDER;
    }

    return {
      fromPosition: move.fromPosition,
      move: move.move,
      quality,
      evaluation: move.evaluation || 0,
      bestMoveEvaluation: move.evaluation || 0,
      evaluationDelta: 0,
      reasoning: move.reasoning || "Unknown",
    };
  }

  /**
   * Create game summary
   */
  private createGameSummary(battleState: BattleState): GameSummary {
    const lightMoves = battleState.moves.filter((m) => m.player === "light");
    const darkMoves = battleState.moves.filter((m) => m.player === "dark");

    return {
      lightSummary: this.createPlayerSummaryFromMoves(
        battleState.lightPlayer,
        lightMoves
      ),
      darkSummary: this.createPlayerSummaryFromMoves(battleState.darkPlayer, darkMoves),
      gameLength: battleState.moves.length,
      openingEvaluation: 0,
      midgameEvaluation: 0,
      endgameEvaluation: 0,
    };
  }

  /**
   * Create player summary from moves
   */
  private createPlayerSummaryFromMoves(
    player: AIPlayerType,
    moves: BattleMove[]
  ): PlayerSummary {
    const analyses = moves.map((m) => this.analyzeMove(m));

    const brilliant = analyses.filter((a) => a.quality === MoveQuality.BRILLIANT).length;
    const good = analyses.filter((a) => a.quality === MoveQuality.GOOD).length;
    const regular = analyses.filter((a) => a.quality === MoveQuality.REGULAR).length;
    const bad = analyses.filter((a) => a.quality === MoveQuality.BAD).length;
    const blunders = analyses.filter((a) => a.quality === MoveQuality.BLUNDER).length;

    const accuracy =
      moves.length > 0 ? (brilliant + good + regular) / moves.length : 0;

    const avgThinkingTime =
      moves.length > 0
        ? moves.reduce((sum, m) => sum + m.thinkingTime, 0) / moves.length
        : 0;

    const avgEval =
      moves.length > 0
        ? moves.reduce((sum, m) => sum + (m.evaluation || 0), 0) / moves.length
        : 0;

    return {
      player,
      totalMoves: moves.length,
      brilliantMoves: brilliant,
      goodMoves: good,
      regularMoves: regular,
      badMoves: bad,
      blunders,
      averageEvaluation: avgEval,
      averageThinkingTime: avgThinkingTime,
      accuracy,
    };
  }

  /**
   * Create empty player summary
   */
  private createEmptyPlayerSummary(player: AIPlayerType): PlayerSummary {
    return {
      player,
      totalMoves: 0,
      brilliantMoves: 0,
      goodMoves: 0,
      regularMoves: 0,
      badMoves: 0,
      blunders: 0,
      averageEvaluation: 0,
      averageThinkingTime: 0,
      accuracy: 0,
    };
  }

  /**
   * Update overall statistics
   */
  private updateOverallStats(): void {
    if (!this.currentBattle) return;

    // Aggregate stats from all games
    const lightStats: PlayerSummary[] = this.currentBattle.games.map(
      (g) => g.summary.lightSummary
    );
    const darkStats: PlayerSummary[] = this.currentBattle.games.map(
      (g) => g.summary.darkSummary
    );

    this.currentBattle.overallStats.light = this.aggregatePlayerStats(lightStats);
    this.currentBattle.overallStats.dark = this.aggregatePlayerStats(darkStats);
  }

  /**
   * Aggregate player statistics
   */
  private aggregatePlayerStats(stats: PlayerSummary[]): PlayerSummary {
    if (stats.length === 0) return this.createEmptyPlayerSummary(AIPlayerType.PLAYER);

    const player = stats[0].player;
    const totalMoves = stats.reduce((sum, s) => sum + s.totalMoves, 0);

    return {
      player,
      totalMoves,
      brilliantMoves: stats.reduce((sum, s) => sum + s.brilliantMoves, 0),
      goodMoves: stats.reduce((sum, s) => sum + s.goodMoves, 0),
      regularMoves: stats.reduce((sum, s) => sum + s.regularMoves, 0),
      badMoves: stats.reduce((sum, s) => sum + s.badMoves, 0),
      blunders: stats.reduce((sum, s) => sum + s.blunders, 0),
      averageEvaluation: stats.reduce((sum, s) => sum + s.averageEvaluation, 0) / stats.length,
      averageThinkingTime: stats.reduce((sum, s) => sum + s.averageThinkingTime, 0) / stats.length,
      accuracy: stats.reduce((sum, s) => sum + s.accuracy, 0) / stats.length,
    };
  }

  /**
   * Calculate head-to-head comparison
   */
  private calculateHeadToHead(): void {
    if (!this.currentBattle || this.currentBattle.gamesPlayed === 0) return;

    const total = this.currentBattle.gamesPlayed;

    this.currentBattle.headToHeadComparison = {
      winRate: {
        light: this.currentBattle.lightWins / total,
        dark: this.currentBattle.darkWins / total,
      },
      averageGameLength:
        this.currentBattle.games.reduce((sum, g) => sum + g.totalMoves, 0) / total,
      averageAccuracy: {
        light: this.currentBattle.overallStats.light.accuracy,
        dark: this.currentBattle.overallStats.dark.accuracy,
      },
      blunderComparison: {
        light: this.currentBattle.overallStats.light.blunders,
        dark: this.currentBattle.overallStats.dark.blunders,
      },
    };
  }
}